import { parseDate, type CalendarDate, type ZonedDateTime } from '@internationalized/date';
import { Box, DatePicker, InlineStack, Select } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import type { NewsletterStatusFilter } from '@/services/db/newsletter/entity';
import type { NewsletterTotals } from '@/services/db/newsletter/queries';
import type { UpdateOptions } from '../use-tab-search';
import { DEFAULT_BROADCASTS_SORT, type NewslettersSearch } from '../url-state';
import { CatalogFilters, CatalogTopicFilter } from './catalog-filters';
import { SortControl, type SortOption } from './list-filters/sort-control';

/** The channels a one-time send can go out on. Custom SMS shares the SMS option with Twilio: the API filters them as one family. */
const CHANNEL_OPTIONS = [
  { label: 'All channels', value: 'all' },
  { label: 'Email', value: 'email' },
  { label: 'Push', value: 'push' },
  { label: 'SMS', value: 'twilio' },
  { label: 'In-App', value: 'in_app' },
  { label: 'Inbox', value: 'inbox' },
  { label: 'Webhook', value: 'webhook' },
];

const STATUS_NAMES = {
  all: 'All one-time sends',
  drafted: 'Drafted',
  sent: 'Sent',
  scheduled: 'Scheduled',
} as const satisfies Record<NewsletterStatusFilter, string>;

const STATUS_ORDER: readonly NewsletterStatusFilter[] = ['all', 'drafted', 'sent', 'scheduled'];

/** Each status filter's sort: sent sends by when they went out, the rest by when they last changed. */
const STATUS_SORTS = {
  all: DEFAULT_BROADCASTS_SORT,
  drafted: DEFAULT_BROADCASTS_SORT,
  scheduled: DEFAULT_BROADCASTS_SORT,
  sent: 'sent',
} as const satisfies Record<NewsletterStatusFilter, string>;

type NewsletterSort = 'messageCount' | 'sent' | 'name' | 'created' | 'updated';

const SORT_OPTIONS: ReadonlyArray<SortOption<NewsletterSort>> = [
  { label: 'Messages created', value: 'messageCount' },
  { label: 'Date sent', value: 'sent' },
  { label: 'Name', value: 'name' },
  { label: 'Date created', value: 'created' },
  { label: 'Last updated', value: 'updated' },
];

/** The label `Broadcast: List Sorted` reports for each sort. */
const SORT_ANALYTICS_LABELS = {
  messageCount: 'Messages Created',
  sent: 'Date Sent',
  name: 'Name',
  created: 'Date Created',
  updated: 'Last Updated',
} as const satisfies Record<NewsletterSort, string>;

/** What the status picker counts before the list has loaded. */
const NO_TOTALS: NewsletterTotals = { sent: 0, drafted: 0, scheduled: 0, all_time: 0 };

/** Each status filter with its count, the way the one-time sends type picker lists them. */
function statusOptions(totals: NewsletterTotals = NO_TOTALS) {
  const counts = {
    all: totals.sent + totals.drafted + totals.scheduled,
    drafted: totals.drafted,
    sent: totals.sent,
    scheduled: totals.scheduled,
  };
  return STATUS_ORDER.map((status) => ({ value: status, label: `${STATUS_NAMES[status]} (${counts[status]})` }));
}

type FilterProps = {
  search: NewslettersSearch;
  onChange: (patch: Partial<NewslettersSearch>, options: UpdateOptions) => void;
};

/** One-time sends by the channel they go out on. */
function ChannelFilter({ search, onChange }: FilterProps) {
  return (
    <Select
      size="sm"
      options={CHANNEL_OPTIONS}
      value={search.channel ?? 'all'}
      ariaLabel="Channel"
      onChange={(channel: string | null) => onChange({ channel: channel ?? undefined }, { replace: false })}
    />
  );
}

/** One-time sends by status, each with its count. A new status starts over on the first page, at its own sort. */
function StatusFilter({ search, onChange, totals }: FilterProps & { totals: NewsletterTotals | undefined }) {
  return (
    <Select
      size="sm"
      options={statusOptions(totals)}
      value={search.type ?? 'all'}
      ariaLabel="Status"
      onChange={(value: string | null) => {
        const type = STATUS_ORDER.find((status) => status === value) ?? 'all';
        onChange({ type, sort: STATUS_SORTS[type], page: undefined }, { replace: false });
      }}
    />
  );
}

type PickedDates = CalendarDate | ZonedDateTime | { start: CalendarDate; end: CalendarDate } | null;

/** One-time sends within a range of days. Only a whole range filters; a half-set one reads as none. */
function DateRangeFilter({ search, onChange }: FilterProps) {
  const range =
    search.startTime == null || search.endTime == null
      ? null
      : { start: parseDate(search.startTime), end: parseDate(search.endTime) };

  const onRangeChange = (value: PickedDates) => {
    if (value == null) {
      onChange({ startTime: undefined, endTime: undefined }, { replace: false });
      return;
    }
    if ('start' in value && 'end' in value) {
      onChange({ startTime: value.start.toString(), endTime: value.end.toString() }, { replace: false });
    }
  };

  return (
    <DatePicker
      size="sm"
      placeholder="Date range"
      ariaLabel="Date range"
      isRange
      withPresets
      isClearable
      value={range}
      onChange={onRangeChange}
      onClear={() => onRangeChange(null)}
    />
  );
}

/** The sort picker, which also reports each new sort by its label. */
function NewslettersSort({ search, onChange }: FilterProps) {
  const { track } = useAnalytics();

  return (
    <SortControl
      options={SORT_OPTIONS}
      selected={search.sort ?? DEFAULT_BROADCASTS_SORT}
      isDescending={!(search.asc ?? false)}
      showLabel={false}
      onChange={(value) => {
        void track('broadcast', 'listSorted', { sorting: SORT_ANALYTICS_LABELS[value] });
        onChange({ sort: value }, { replace: false });
      }}
      onDirectionChange={(isDescending) => onChange({ asc: !isDescending }, { replace: false })}
    />
  );
}

type NewslettersFiltersProps = FilterProps & {
  /** The list's per-status counts, which the type picker shows beside each status. */
  totals: NewsletterTotals | undefined;
};

/**
 * The one-time sends tab's filters: channel, status, date range, tags, goals, object types and
 * topic, with the sort at the far end. The goal, object type and topic pickers only appear when
 * there is something to pick from.
 */
export function NewslettersFilters({ search, totals, onChange }: NewslettersFiltersProps) {
  return (
    <InlineStack alignBlock="center" gap="100">
      <ChannelFilter search={search} onChange={onChange} />
      <StatusFilter search={search} onChange={onChange} totals={totals} />
      <DateRangeFilter search={search} onChange={onChange} />
      <CatalogFilters search={search} onChange={onChange} />
      <CatalogTopicFilter search={search} onChange={onChange} />
      <Box flex={1} maxW="full" minW="0" />
      <Box flexShrink="0">
        <NewslettersSort search={search} onChange={onChange} />
      </Box>
    </InlineStack>
  );
}
